import React, { useState, useRef } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Upload as UploadIcon,
  CloudUpload as CloudUploadIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Delete as DeleteIcon,
  People as PeopleIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

interface EmployeeData {
  firstName: string;
  lastName: string;
  email: string;
  office?: string;
  department?: string;
  position?: string;
  accountType?: 'corporate' | 'invited';
}

interface ValidationResult {
  valid: EmployeeData[];
  invalid: Array<{
    row?: number;
    data: EmployeeData;
    reason: string;
  }>;
  summary: {
    total: number;
    valid: number;
    invalid: number;
    corporate?: number;
    invited?: number;
  };
}

interface CreationResult {
  created: Array<{
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  }>;
  failed: Array<{
    firstName: string;
    lastName: string;
    email: string;
    reason: string;
  }>;
  summary: {
    total: number;
    created: number;
    failed: number;
  };
}

const BulkUploadEmployees: React.FC = () => {
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [creationResult, setCreationResult] = useState<CreationResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });

  const steps = ['Subir Archivo', 'Revisar Datos', 'Confirmar y Procesar', 'Resultado'];

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.name.endsWith('.csv')) {
        setError('Por favor selecciona un archivo CSV');
        return;
      }
      setSelectedFile(file);
      setError(null);
      setValidationResult(null);
      setCreationResult(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Por favor selecciona un archivo');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await api.validateBulkUpload(formData);
      setValidationResult(response);
      
      // Avanzar al paso 1 si hay datos para revisar (válidos o inválidos)
      if (response.summary.total > 0) {
        setActiveStep(1);
        if (response.valid.length === 0 && response.invalid.length > 0) {
          setError('No hay empleados válidos para procesar. Revisa los errores en la tabla a continuación.');
        }
      } else {
        setError('El archivo está vacío o no contiene datos válidos.');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error procesando archivo');
    } finally {
      setLoading(false);
    }
  };

  const handleProcess = async () => {
    if (!validationResult || validationResult.valid.length === 0) {
      setError('No hay empleados válidos para procesar');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await api.createBulkEmployees(validationResult.valid);
      setCreationResult(response);
      setActiveStep(3);

      setSnackbar({
        open: true,
        message: `Se crearon ${response.created.length} de ${response.summary.total} empleados exitosamente`,
        severity: response.failed.length > 0 ? 'error' : 'success'
      });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error creando empleados');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setActiveStep(0);
    setSelectedFile(null);
    setValidationResult(null);
    setCreationResult(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDownloadTemplate = () => {
    const csvContent = 'Nombre,Apellido,Email,Sede,Departamento,Cargo,Tipo\nJuan,Pérez,juan.perez@empresa.com,Sede Central,Recursos Humanos,Gerente de RH,Corporativo\nMaría,García,maria.garcia@gmail.com,Sede Norte,Ventas,Consultora Externa,Invitado';
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'plantilla_empleados.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box mb={4}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box display="flex" alignItems="center" gap={2}>
            <PeopleIcon sx={{ fontSize: 40, color: 'primary.main' }} />
            <Typography variant="h4" component="h1">
              Carga Masiva de Empleados
            </Typography>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Stepper */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </CardContent>
        </Card>

        {/* Paso 1: Subir Archivo */}
        {activeStep === 0 && (
          <Card>
            <CardContent>
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <DescriptionIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
                <Typography variant="h5" gutterBottom>
                  Sube un archivo CSV con los empleados
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                  Columnas: Nombre, Apellido, Email, Sede, Departamento, Cargo (opcional), Tipo (opcional: Corporativo o Invitado).
                  Puede incluir correos corporativos (@empresa) e invitados con correo personal (Gmail, Outlook, etc.).
                  Use la plantilla UTF-8 o guarde el CSV en Excel como «CSV UTF-8» para conservar tildes y la letra ñ.
                </Typography>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />

                <Box sx={{ mb: 3 }}>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<CloudUploadIcon />}
                    size="large"
                    sx={{ mr: 2 }}
                  >
                    Seleccionar Archivo CSV
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handleFileSelect}
                      style={{ display: 'none' }}
                    />
                  </Button>

                  <Button
                    variant="outlined"
                    onClick={handleDownloadTemplate}
                    size="large"
                  >
                    Descargar Plantilla
                  </Button>
                </Box>

                {selectedFile && (
                  <Box sx={{ mt: 3 }}>
                    <Chip
                      label={selectedFile.name}
                      onDelete={() => {
                        setSelectedFile(null);
                        if (fileInputRef.current) {
                          fileInputRef.current.value = '';
                        }
                      }}
                      deleteIcon={<DeleteIcon />}
                    />
                  </Box>
                )}

                {selectedFile && (
                  <Box sx={{ mt: 3 }}>
                    <Button
                      variant="contained"
                      size="large"
                      onClick={handleUpload}
                      disabled={loading}
                      startIcon={<UploadIcon />}
                    >
                      {loading ? 'Procesando...' : 'Validar Archivo'}
                    </Button>
                  </Box>
                )}

                {loading && (
                  <Box sx={{ mt: 3 }}>
                    <LinearProgress />
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Paso 2: Revisar Datos */}
        {activeStep === 1 && validationResult && (() => {
          // Combinar todos los datos (válidos e inválidos) para mostrar en una sola tabla
          const allData = [
            ...validationResult.invalid.map(item => ({ ...item.data, hasError: true, reason: item.reason })),
            ...validationResult.valid.map(item => ({ ...item, hasError: false, reason: null }))
          ];

          return (
            <Card>
              <CardContent>
                <Box mb={3}>
                  <Typography variant="h6" gutterBottom>
                    Revisión de Datos
                  </Typography>
                  <Box display="flex" gap={2} mb={2}>
                    <Chip
                      label={`Válidos: ${validationResult.summary.valid}`}
                      color="success"
                    />
                    <Chip
                      label={`Inválidos: ${validationResult.summary.invalid}`}
                      color="error"
                    />
                    <Chip
                      label={`Total: ${validationResult.summary.total}`}
                    />
                    {(validationResult.summary.corporate !== undefined || validationResult.summary.invited !== undefined) && (
                      <>
                        <Chip
                          label={`Corporativos: ${validationResult.summary.corporate ?? 0}`}
                          color="primary"
                          variant="outlined"
                        />
                        <Chip
                          label={`Invitados: ${validationResult.summary.invited ?? 0}`}
                          color="secondary"
                          variant="outlined"
                        />
                      </>
                    )}
                  </Box>
                </Box>

                <Box>
                  <Typography variant="subtitle1" gutterBottom>
                    Listado de Empleados a Cargar ({allData.length})
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 500 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Estado</strong></TableCell>
                          <TableCell><strong>Nombre</strong></TableCell>
                          <TableCell><strong>Apellido</strong></TableCell>
                          <TableCell><strong>Email</strong></TableCell>
                          <TableCell><strong>Tipo</strong></TableCell>
                          <TableCell><strong>Sede</strong></TableCell>
                          <TableCell><strong>Departamento</strong></TableCell>
                          <TableCell><strong>Cargo</strong></TableCell>
                          <TableCell><strong>Error/Observación</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {allData.map((item, index) => (
                          <TableRow 
                            key={index}
                            sx={{
                              backgroundColor: item.hasError ? 'rgba(211, 47, 47, 0.1)' : 'transparent',
                              '&:hover': {
                                backgroundColor: item.hasError ? 'rgba(211, 47, 47, 0.2)' : 'action.hover'
                              }
                            }}
                          >
                            <TableCell>
                              {item.hasError ? (
                                <Chip
                                  icon={<ErrorIcon />}
                                  label="Error"
                                  color="error"
                                  size="small"
                                />
                              ) : (
                                <Chip
                                  icon={<CheckCircleIcon />}
                                  label="Válido"
                                  color="success"
                                  size="small"
                                />
                              )}
                            </TableCell>
                            <TableCell>{item.firstName}</TableCell>
                            <TableCell>{item.lastName}</TableCell>
                            <TableCell>{item.email}</TableCell>
                            <TableCell>
                              {!item.hasError && (
                                <Chip
                                  label={item.accountType === 'invited' ? 'Invitado' : 'Corporativo'}
                                  size="small"
                                  color={item.accountType === 'invited' ? 'secondary' : 'primary'}
                                  variant={item.accountType === 'invited' ? 'outlined' : 'filled'}
                                />
                              )}
                            </TableCell>
                            <TableCell>{item.office || '-'}</TableCell>
                            <TableCell>{item.department || '-'}</TableCell>
                            <TableCell>{item.position || '-'}</TableCell>
                            <TableCell>
                              {item.hasError ? (
                                <Chip
                                  label={item.reason}
                                  color="error"
                                  size="small"
                                />
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  -
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Box display="flex" justifyContent="space-between" mt={3}>
                  <Button onClick={handleReset}>
                    Cancelar
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => setActiveStep(2)}
                    disabled={validationResult.valid.length === 0}
                  >
                    Continuar
                  </Button>
                </Box>
              </CardContent>
            </Card>
          );
        })()}

        {/* Paso 3: Confirmar */}
        {activeStep === 2 && validationResult && (
          <Card>
            <CardContent>
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h5" gutterBottom>
                  Confirmar Creación de Empleados
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                  Se crearán {validationResult.valid.length} empleados y se les enviará un correo de bienvenida
                  con un enlace para establecer su contraseña (válido por 24 horas).
                </Typography>

                <Box display="flex" justifyContent="center" gap={2}>
                  <Button onClick={() => setActiveStep(1)}>
                    Volver
                  </Button>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleProcess}
                    disabled={loading}
                  >
                    {loading ? 'Procesando...' : 'Procesar Empleados'}
                  </Button>
                </Box>

                {loading && (
                  <Box sx={{ mt: 3 }}>
                    <LinearProgress />
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Paso 4: Resultado */}
        {activeStep === 3 && creationResult && (
          <Card>
            <CardContent>
              <Box mb={3}>
                <Typography variant="h6" gutterBottom>
                  Resultado de la Carga
                </Typography>
                <Box display="flex" gap={2} mb={2}>
                  <Chip
                    icon={<CheckCircleIcon />}
                    label={`Creados: ${creationResult.summary.created}`}
                    color="success"
                  />
                  <Chip
                    icon={<ErrorIcon />}
                    label={`Fallidos: ${creationResult.summary.failed}`}
                    color="error"
                  />
                  <Chip
                    label={`Total: ${creationResult.summary.total}`}
                  />
                </Box>
              </Box>

              {creationResult.created.length > 0 && (
                <Box mb={4}>
                  <Typography variant="subtitle1" gutterBottom>
                    Empleados Creados Exitosamente ({creationResult.created.length})
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Nombre</strong></TableCell>
                          <TableCell><strong>Apellido</strong></TableCell>
                          <TableCell><strong>Email</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {creationResult.created.map((employee) => (
                          <TableRow key={employee._id}>
                            <TableCell>{employee.firstName}</TableCell>
                            <TableCell>{employee.lastName}</TableCell>
                            <TableCell>{employee.email}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {creationResult.failed.length > 0 && (
                <Box>
                  <Typography variant="subtitle1" gutterBottom color="error">
                    Empleados que No se Pudieron Crear ({creationResult.failed.length})
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Nombre</strong></TableCell>
                          <TableCell><strong>Apellido</strong></TableCell>
                          <TableCell><strong>Email</strong></TableCell>
                          <TableCell><strong>Razón</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {creationResult.failed.map((item, index) => (
                          <TableRow key={index}>
                            <TableCell>{item.firstName}</TableCell>
                            <TableCell>{item.lastName}</TableCell>
                            <TableCell>{item.email}</TableCell>
                            <TableCell>
                              <Chip
                                label={item.reason}
                                color="error"
                                size="small"
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              <Box display="flex" justifyContent="center" mt={3}>
                <Button
                  variant="contained"
                  onClick={handleReset}
                >
                  Cargar Otro Archivo
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default BulkUploadEmployees;

